import React from "react";
import MarketingNav from "@/components/MarketingNav";
import QuoteCTA from "@/components/QuoteCTA";
import Seo from "@/components/Seo";
import SiteFooter from "@/components/SiteFooter";
import QuoteForm from "@/components/QuoteForm";
import { BIZ } from "@/data/site";
import { Phone, Mail, MessageSquare, MapPin, Clock, ExternalLink } from "lucide-react";

export default function ContactPage() {
  return (
    <div className="bg-paper min-h-screen">
      <Seo
        title="Contact Wynridge Exterior Cleaning"
        description={`Call, text, or email Wynridge Exterior Cleaning in ${BIZ.city}, ${BIZ.stateShort}. 24/7 availability, same-day estimates.`}
        path="/contact/"
      />
      <MarketingNav />
      <QuoteCTA />

      <section className="pt-6 pb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-10 items-start">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-brand-700 mb-4">Contact</div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight text-stone-900 leading-[1.05]" style={{ fontFamily: "Outfit" }}>
              Get in touch<br /><span className="text-electric-500 italic">any way you like.</span>
            </h1>
            <p className="mt-6 text-lg text-stone-600">Call, text, email, or drop the form. We&apos;re around 24/7 for questions and quotes.</p>

            <div className="mt-10 space-y-4">
              <ContactRow icon={Phone} label="Call" value={BIZ.phone} href={`tel:${BIZ.phoneTel}`} testId="contact-call" />
              <ContactRow icon={MessageSquare} label="Text" value={BIZ.phone} href={BIZ.phoneSmsHref} testId="contact-text" />
              <ContactRow icon={Mail} label="Email" value={BIZ.email} href={`mailto:${BIZ.email}`} testId="contact-email" />
              <ContactRow icon={MapPin} label="Base" value={`${BIZ.city}, ${BIZ.stateShort} ${BIZ.zip}`} note="Service-area business — no walk-in location" />
              <ContactRow icon={Clock} label="Hours" value="24 / 7 availability" />
            </div>

            <a
              href={BIZ.gbpUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-gbp-link"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-brand-600 hover:bg-electric-500 text-white font-bold px-6 py-3 shadow-lg transition-all hover:-translate-y-0.5"
            >
              See us on Google
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>

          {/* Map placeholder */}
          <div
            data-testid="contact-map-placeholder"
            className="rounded-3xl aspect-square lg:aspect-auto lg:h-full min-h-[400px] bg-gradient-to-br from-brand-100 to-stone-200 border border-stone-200 flex flex-col items-center justify-center text-brand-700 relative overflow-hidden"
          >
            <div className="absolute inset-0 opacity-25" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #1B2D5B 0%, transparent 30%), radial-gradient(circle at 70% 60%, #2E7CE0 0%, transparent 40%)" }} />
            <MapPin className="w-14 h-14 relative" />
            <div className="mt-4 font-bold relative" style={{ fontFamily: "Outfit" }}>{BIZ.city}, {BIZ.stateShort}</div>
            <div className="text-xs uppercase tracking-widest opacity-70 mt-1 relative">Service area map</div>
            <div className="text-[10px] mt-1 opacity-60 relative">[embed Google Map later]</div>
          </div>
        </div>
      </section>

      <QuoteForm />
      <SiteFooter />
    </div>
  );
}

function ContactRow({ icon: Icon, label, value, href, note, testId }) {
  const inner = (
    <div className="flex items-start gap-3">
      <div className="w-10 h-10 rounded-xl bg-brand-100 flex items-center justify-center flex-shrink-0">
        <Icon className="w-4 h-4 text-brand-700" />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">{label}</div>
        <div className="font-bold text-stone-900" style={{ fontFamily: "Outfit" }}>{value}</div>
        {note && <div className="text-xs text-stone-500 mt-0.5">{note}</div>}
      </div>
    </div>
  );
  return href ? (
    <a href={href} data-testid={testId} className="block hover:bg-stone-50 rounded-xl -mx-2 px-2 py-2 transition-colors">{inner}</a>
  ) : (
    <div className="rounded-xl -mx-2 px-2 py-2">{inner}</div>
  );
}
